import { z } from 'zod';
import { EntityType } from '__types__/entities.d';
import { RateInterval } from '__types__/enums';
import { MetastructureFieldType } from '__types__/v1/models/Metastructure.d';
import { searchApiPagingFromHttp$ } from 'v5/api/_common/query/pagination.http.schema';
import type { ValidatedRow } from 'v5/platform/row-level-validation';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import type { ResourceListResponse } from './resource.api';

// --- HTTP date coercion (JSON → Date), aligned with booking.api.http.schema ---

function safeDatePreprocess(val: unknown): Date | null | undefined {
  if (val == null) return val as null | undefined;
  if (typeof val === 'string') return new Date(val);
  if (typeof val === 'number') return new Date(val * 1000);
  return undefined;
}

/**
 * Coerces JSON date values for required `z.date()` fields.
 * Must not throw: row-level parsing uses `safeParse`; throwing would surface as a
 * fatal parse error instead of marking the row invalid.
 */
function requiredDatePreprocess(val: unknown): Date | undefined {
  if (val == null) return undefined;
  if (typeof val === 'string') return new Date(val);
  if (typeof val === 'number') return new Date(val * 1000);
  return undefined;
}

const entityId$ = z.number();

const resourceTypeModel$ = z.enum([
  'INDIVIDUAL',
  'ORGANIZATION',
  'LOCATION',
  'ITEM',
  'TALENT',
  'AGENT'
]);

const contactType$ = resourceTypeModel$;

const searchQueryFromHttp$ = z.object({
  page: z.number().nullish(),
  order_by: z
    .object({
      field: z.string(),
      direction: z.enum(['asc', 'desc'])
    })
    .nullish(),
  filters: z.record(z.string(), z.any()).nullish()
});

const resourceDetailsContactType$ = z.enum([
  'work',
  'personal',
  'social',
  'other',
  'agent',
  'guardian'
]);

const resourceDetailsSocialType$ = z.enum([
  'angellist',
  'behance',
  'dribbble',
  'facebook',
  'github',
  'instagram',
  'linkedin',
  'medium',
  'rednote',
  'soundcloud',
  'spotify',
  'twitter',
  'vimeo',
  'vsco',
  'youtube',
  'tiktok',
  'discord',
  'other'
]);

const metastructureField$ = z.union([
  z.object({
    type: z.literal(MetastructureFieldType.Core),
    active: z.boolean(),
    locked: z.boolean(),
    default: z.string(),
    required: z.boolean(),
    name: z.string()
  }),
  z.object({
    type: z.literal(MetastructureFieldType.Custom),
    active: z.boolean(),
    locked: z.boolean(),
    default: z.string(),
    required: z.boolean(),
    custom_field_definition_id: entityId$
  }),
  z.object({
    type: z.literal(MetastructureFieldType.CustomGroup),
    active: z.boolean(),
    locked: z.boolean(),
    default: z.string(),
    required: z.boolean(),
    custom_fieldgroup_definition_id: entityId$
  })
]);

/** Mirrors `Metastructure` (v1 models). */
const metastructureFromHttp$ = z.object({
  archived: z.boolean().nullish(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullish(),
  entity_type: z.nativeEnum(EntityType).nullish(),
  fields: z.array(metastructureField$),
  field_order: z.array(z.string()).nullish()
});

/** Mirrors `BookingType` (v1 models). */
const bookingTypeFromHttp$ = z.object({
  id: entityId$,
  archived: z.boolean(),
  order: z.number().nullable(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(requiredDatePreprocess, z.date()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  name: z.string().nullish(),
  icon: z.string().nullish(),
  model: z.string().nullish(),
  metastructure: metastructureFromHttp$,
  include_weekends_default: z.boolean().nullish(),
  default_all_day_hours: z.number().nullish()
});

/** Mirrors `Tag` (v1 models). */
const tagFromHttp$ = z.object({
  id: entityId$,
  tag: z.string(),
  tag_type: z.enum(['resource', 'document', 'production', 'shortlist'])
});

/** Mirrors global `Address` (data types). */
const addressFromHttp$ = z.object({
  street_address: z.string().nullish(),
  locality: z.string().nullish(),
  city: z.string().nullish(),
  region: z.string().nullish(),
  post_code: z.string().nullish(),
  country: z.string().nullish()
});

/** `Resource.emails` / `websites` / `phone_numbers` rows. */
const resourceDetailContactFromHttp$ = z.object({
  id: entityId$,
  contact_id: entityId$,
  detail_type: z.enum(['PHONE', 'SOCIAL', 'WEBSITE', 'EMAIL']),
  value_1: z.string().nullish(),
  value_2: resourceDetailsContactType$.nullish(),
  value_3: z.string().nullish()
});

/** `Resource.socials` rows. */
const resourceDetailSocialFromHttp$ = z.object({
  id: entityId$,
  contact_id: entityId$,
  detail_type: z.enum(['PHONE', 'SOCIAL', 'WEBSITE', 'EMAIL']),
  value_1: z.string().nullish(),
  value_2: resourceDetailsSocialType$.nullish(),
  value_3: z.string().nullish()
});

/** Mirrors `Location` (v1 models). */
const locationFromHttp$ = z.object({
  id: entityId$,
  archived: z.boolean(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(safeDatePreprocess, z.date().nullish()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  name: z.string().nullish(),
  address: z.record(z.string(), z.unknown()).nullish(),
  coordinates: z
    .object({
      lat: z.number().nullish(),
      lon: z.number().nullish()
    })
    .nullish()
});

/** Mirrors `Group` (v1 models). */
const groupFromHttp$ = z.object({
  id: entityId$,
  archived: z.boolean(),
  order: z.number().nullable(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(requiredDatePreprocess, z.date()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  name: z.string().nullish(),
  entity_type: z.nativeEnum(EntityType).nullish(),
  entity_id: entityId$.nullish(),
  query: searchQueryFromHttp$.nullish(),
  include_in_calculation: z.boolean(),
  colour: z.string().nullish()
});

/** Matches {@link CustomField} `entity_type` (EntityTypesWithCustomFields). */
const customFieldEntityTypeFromHttp$ = z.union([
  z.literal(EntityType.BOOKING),
  z.literal(EntityType.CONTACT),
  z.literal(EntityType.EXPENSE),
  z.literal(EntityType.PRODUCTION),
  z.literal(EntityType.SHORTLIST)
]);

/** Mirrors `CustomField` (v1 models). */
const customFieldFromHttp$ = z.object({
  id: entityId$,
  archived: z.boolean(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(safeDatePreprocess, z.date().nullish()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  custom_field_definition_id: entityId$,
  entity_type: customFieldEntityTypeFromHttp$,
  entity_id: entityId$,
  boolean_value: z.boolean().nullable(),
  string_value: z.string().nullable(),
  integer_value: z.number().nullable(),
  float_value: z.number().nullable(),
  date_value: z.preprocess(safeDatePreprocess, z.date().nullable()),
  raw_value: z.string().nullable(),
  raw_unit: z.string().nullable(),
  option_id: entityId$.nullable(),
  multi_select_ids: z.array(entityId$).nullable()
});

/** Mirrors `CustomFieldGroup` (v1 models). */
const customFieldGroupFromHttp$ = z.object({
  id: entityId$,
  archived: z.boolean(),
  order: z.number().nullable(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(requiredDatePreprocess, z.date()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  custom_fieldgroup_definition_id: entityId$,
  entity_id: entityId$,
  entity_type: z.nativeEnum(EntityType),
  custom_fields: z.array(customFieldFromHttp$)
});

/** Mirrors `Rate` (v1 models). */
const rateFromHttp$ = z.object({
  id: entityId$,
  archived: z.boolean(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(requiredDatePreprocess, z.date()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  currency: z.string().nullish(),
  amount: z.number().nullish(),
  interval: z.nativeEnum(RateInterval).nullish(),
  agency_fee: z.number().nullish(),
  payroll_fee: z.number().nullish(),
  processing_fee: z.number().nullish()
});

/** Mirrors `ResourceRate` (v1 models). */
const resourceRateFromHttp$ = z.object({
  id: entityId$,
  order: z.number().nullable(),
  contact_id: entityId$,
  rate_id: entityId$.nullish(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  currency: z.string().nullish(),
  amount: z.number().nullish(),
  interval: z.nativeEnum(RateInterval),
  agency_fee: z.number().nullish(),
  payroll_fee: z.number().nullish(),
  processing_fee: z.number().nullish(),
  rate: rateFromHttp$.nullish()
});

/**
 * Mirrors `ResourceType` (v1 models). Objection may send `null` for optional or
 * boolean columns; we coerce to match {@link ResourceType} (no `null` on domain).
 */
const resourceTypeFromHttp$ = z.object({
  id: entityId$,
  /** `null` / missing → `false` (Archivable.archived is boolean). */
  archived: z.boolean().nullish().default(false),
  order: z.number().nullable(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  /** `null` from API → epoch (required `Date` on {@link ResourceType}). */
  created_at: z
    .preprocess(safeDatePreprocess, z.date().nullable())
    .transform((d): Date => d ?? new Date(0)),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  entity_type: z.nativeEnum(EntityType).nullish(),
  name: z.string().nullish(),
  icon: z.string().nullish(),
  model: resourceTypeModel$.nullish(),
  /** `null` → `undefined` (optional boolean on domain type). */
  internal: z
    .boolean()
    .nullish()
    .transform((v): boolean | undefined => (v === null ? undefined : v)),
  metastructure: metastructureFromHttp$
});

/** Mirrors `File` (v1 models). */
const fileFromHttp$ = z.object({
  id: entityId$,
  archived: z.boolean(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(requiredDatePreprocess, z.date()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  name: z.string().nullish(),
  original_url: z.string().nullish(),
  size: z.number().nullish(),
  mime_type: z.string().nullish(),
  provider: z.string().nullish(),
  key: z.string().nullish(),
  bucket: z.string().nullish(),
  region: z.string().nullish(),
  public: z.boolean().nullish(),
  url: z.string().nullish(),
  resize_url: z.string().nullish()
});

/** Mirrors `Document` (v1 models). */
const documentFromHttp$ = z.object({
  id: entityId$,
  archived: z.boolean(),
  order: z.number().nullable(),
  legacy_user_id: z.number().nullish(),
  user_id: z.string().nullable(),
  created_at: z.preprocess(requiredDatePreprocess, z.date()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  file_id: entityId$.nullish(),
  entity_type: z.nativeEnum(EntityType),
  entity_id: entityId$,
  url: z.string().nullish(),
  document_type: z.enum(['CONTRACT', 'INVOICE', 'RECEIPT']).nullish(),
  status: z
    .enum(['AWAITING_APPROVAL', 'APPROVED', 'PAID', 'DECLINED'])
    .nullish(),
  provider: z.string().nullish(),
  document_data: z
    .object({
      amount: z.number().nullish(),
      reference: z.string().nullish()
    })
    .nullish(),
  file: fileFromHttp$.nullish()
});

const eventType$ = z.enum([
  'BOOKING',
  'RESOURCE_SLOT',
  'RESOURCE_SLOT_ASSIGNMENT',
  'PRODUCTION',
  'PRODUCTION_MILESTONE',
  'PRODUCTION_SCHEDULE',
  'SHORTLIST_DATE'
]);

const eventDateType$ = z.enum([
  'RECURRING_DEFAULT',
  'RECURRING_HOURS',
  'RECURRING_SPECIFIC_HOURS',
  'INCLUSIVE_HOURS'
]);

/** Mirrors `Event` (v1 models). */
const eventFromHttp$ = z.object({
  id: entityId$,
  user_id: z.string().nullable(),
  legacy_user_id: z.number().nullish(),
  created_at: z.preprocess(requiredDatePreprocess, z.date()),
  updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
  created_mechanism: z.string().nullable(),
  event_type: eventType$,
  entity_type: z.nativeEnum(EntityType).nullish(),
  entity_id: entityId$.nullish(),
  note: z.string().nullish(),
  timezone: z.string().nullish(),
  date_type: eventDateType$,
  all_day: z.boolean().nullish(),
  start_date: z.string().nullish(),
  start_timestamp: z.preprocess(
    safeDatePreprocess,
    z.date().optional().nullable()
  ),
  end_date: z.string().nullish(),
  end_timestamp: z.preprocess(
    safeDatePreprocess,
    z.date().optional().nullable()
  ),
  recurrence: z.string().nullish(),
  estimated_daily_hours: z.number().nullish(),
  estimated_daily_minutes: z.number().nullish(),
  estimated_start_time: z.string().nullish(),
  estimated_end_time: z.string().nullish(),
  all_day_duration_hours: z.number(),
  include_weekends: z.boolean().nullish(),
  status_id: entityId$.nullish(),
  status: z.string().nullish()
});

/** Mirrors `ResourceSlot` (v1 models). */
const resourceSlotFromHttp$ = z.object({
  id: entityId$,
  order: z.number().nullable(),
  production_id: entityId$.nullish(),
  group_id: entityId$.nullish(),
  name: z.string().nullish(),
  query: searchQueryFromHttp$.nullish(),
  note: z.string().nullish(),
  include_in_calculation: z.boolean(),
  slot_category_id: entityId$.nullish(),
  is_location: z.boolean(),
  estimate_rate_id: entityId$.nullish(),
  estimate_rate_currency: z.string().nullish(),
  estimate_rate_amount: z.number().nullish(),
  estimate_rate_interval: z.nativeEnum(RateInterval).nullish(),
  estimate_rate_quantity: z.number().nullish(),
  estimate_rate_agency_fee: z.number().nullish(),
  estimate_rate_payroll_fee: z.number().nullish(),
  estimate_rate_processing_fee: z.number().nullish()
});

/** Mirrors `ResourceSlotAssignment` (v1 models). */
const resourceSlotAssignmentFromHttp$ = z.object({
  id: entityId$,
  resource_slot_id: entityId$.nullish(),
  contact_id: entityId$.nullish(),
  booking_id: entityId$.nullish(),
  events: z.array(eventFromHttp$).nullish().default([]),
  resource_slot: resourceSlotFromHttp$.nullish()
});

/**
 * Mirrors `Booking` (v1 models). `contact` is lazy-linked to the parent `Resource` shape.
 */
const bookingFromHttp$ = z.lazy(() =>
  z.object({
    id: entityId$,
    archived: z.boolean(),
    legacy_user_id: z.number().nullish(),
    user_id: z.string().nullable(),
    created_at: z.preprocess(requiredDatePreprocess, z.date()),
    updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
    created_mechanism: z.string().nullable(),
    custom_fields: z.array(customFieldFromHttp$).nullish().default([]),
    custom_fieldgroups: z
      .array(customFieldGroupFromHttp$)
      .nullish()
      .default([]),
    guid: z.string(),
    entity_type: z.nativeEnum(EntityType),
    booking_type_id: entityId$,
    contact_id: entityId$.nullish(),
    status_id: entityId$.nullish(),
    contact_rate_id: entityId$.nullish(),
    rate_id: entityId$.nullish(),
    rate_interval: z.nativeEnum(RateInterval).nullish(),
    rate_currency: z.string().nullish(),
    rate_amount: z.number().nullish(),
    rate_agency_fee: z.number().nullish(),
    rate_payroll_fee: z.number().nullish(),
    rate_processing_fee: z.number().nullish(),
    include_weekends: z.boolean().nullish(),
    estimated_daily_hours: z.number().nullish(),
    start_date: z.preprocess(
      safeDatePreprocess,
      z.date().optional().nullable()
    ),
    end_date: z.preprocess(safeDatePreprocess, z.date().optional().nullable()),
    licence: z.string().nullish(),
    note: z.string().nullish(),
    submitted_date: z.preprocess(
      safeDatePreprocess,
      z.date().optional().nullable()
    ),
    agreed_date: z.preprocess(
      safeDatePreprocess,
      z.date().optional().nullable()
    ),
    pencilled_at: z.preprocess(
      safeDatePreprocess,
      z.date().optional().nullable()
    ),
    confirmed_at: z.preprocess(
      safeDatePreprocess,
      z.date().optional().nullable()
    ),
    cancelled_at: z.preprocess(
      safeDatePreprocess,
      z.date().optional().nullable()
    ),
    confirmation_sent_at: z.preprocess(
      safeDatePreprocess,
      z.date().optional().nullable()
    ),
    cancellation_sent_at: z.preprocess(
      safeDatePreprocess,
      z.date().optional().nullable()
    ),
    tasklist_id: entityId$.nullish(),
    contracts: z.array(documentFromHttp$).nullish().default([]),
    invoices: z.array(documentFromHttp$).nullish().default([]),
    events: z.array(eventFromHttp$).nullish().default([]),
    contact: z.lazy(() => resourceListItemFromHttp$),
    booking_type: bookingTypeFromHttp$,
    resource_slot_assignments: z
      .array(resourceSlotAssignmentFromHttp$)
      .nullish()
      .default([])
  })
);

/**
 * One contact row from `/search/contacts`, aligned with v1 `Resource`.
 * Nested `organisation` / `represented_by` recurse via `z.lazy` (same row shape as root).
 */
export const resourceListItemFromHttp$ = z.lazy(() =>
  z.object({
    id: entityId$,
    archived: z.boolean(),
    legacy_user_id: z.number().nullish(),
    user_id: z.string().nullable(),
    created_at: z.preprocess(requiredDatePreprocess, z.date()),
    updated_at: z.preprocess(safeDatePreprocess, z.date().nullable()),
    created_mechanism: z.string().nullable(),
    custom_fields: z.array(customFieldFromHttp$).nullish().default([]),
    custom_fieldgroups: z
      .array(customFieldGroupFromHttp$)
      .nullish()
      .default([]),
    guid: z.string(),
    entity_type: z.nativeEnum(EntityType),
    // Optional Objection columns may be omitted, undefined, or null.
    contact_type: contactType$.nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    nick_name: z.string().nullish(),
    pronouns: z.string().nullish(),
    pronouns_option_id: z.number().nullish(),
    full_name: z.string(),
    profile_picture: z.string().nullish(),
    profile_picture_id: z.number().nullish(),
    role: z.string().nullish(),
    address: addressFromHttp$.nullish(),
    import_id: entityId$.nullish(),
    resource_type_id: entityId$.nullish(),
    organisation_id: entityId$.nullish(),
    represented_by_id: entityId$.nullish(),
    enterprise_workspace_id: z.number().nullish(),
    account_id: z.string().nullish(),
    tasklist_id: entityId$.nullish(),
    locations: z.array(locationFromHttp$).nullish().default([]),
    emails: z.array(resourceDetailContactFromHttp$).nullish().default([]),
    websites: z.array(resourceDetailContactFromHttp$).nullish().default([]),
    phone_numbers: z
      .array(resourceDetailContactFromHttp$)
      .nullish()
      .default([]),
    socials: z.array(resourceDetailSocialFromHttp$).nullish().default([]),
    tags: z.array(tagFromHttp$).nullish().default([]),
    groups: z.array(groupFromHttp$).nullish().default([]),
    rates: z.array(resourceRateFromHttp$).nullish().default([]),
    organisation: z.lazy(() => resourceListItemFromHttp$).nullish(),
    represented_by: z.lazy(() => resourceListItemFromHttp$).nullish(),
    resource_type: resourceTypeFromHttp$.nullish(),
    bookings: z.array(bookingFromHttp$).nullish().default([]),
    resource_type_model: resourceTypeModel$.nullish()
  })
);

export type ResourceListItemFromHttp = z.output<
  typeof resourceListItemFromHttp$
>;

/**
 * Full `/search/contacts` JSON: envelope + paging, then per-row validation into
 * `ResourceListResponse`.
 */
export const resourceContactSearchListResponse$ = z
  .object({
    results: rowLevelValidation.withRowLevelValidation(
      resourceListItemFromHttp$
    ),
    paging: searchApiPagingFromHttp$
  })
  .transform(
    (data): ResourceListResponse => ({
      items: data.results,
      count: data.paging.total
    })
  );
